/**
 * KNK-1.37 / FR-31 — counsel's review of the location policy, inside the readiness checklist's
 * `location_policy` row.
 *
 * Shows the policy text exactly as reps read it on the phone (location_policy()), so it can be sent
 * to counsel, and records their review. ⚠️ Nothing here is the gate: set_knock_readiness_item
 * refuses the attestation without a review of the current version (migration 20261009b2).
 */
import { useState } from 'react';
import { Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { KNOCK_MODE_COPY, parseStatesInput, reviewLine, type KnockModeStatus, type KnockReadinessItem } from '@/lib/knockMode';
import { fetchLocationPolicy, recordLocationPolicyReview, type LocationPolicyText } from '@/services/knockModeService';

const COPY = KNOCK_MODE_COPY.policy;

export function LocationPolicyReview({
  item,
  canManage,
  onStatus,
  onError,
}: {
  item: KnockReadinessItem;
  canManage: boolean;
  onStatus: (s: KnockModeStatus) => void;
  onError: (e: unknown) => void;
}) {
  const [policy, setPolicy] = useState<LocationPolicyText | null>(null);
  const [showText, setShowText] = useState(false);
  const [loadingText, setLoadingText] = useState(false);
  const [editing, setEditing] = useState(false);
  const [states, setStates] = useState('');
  const [reviewer, setReviewer] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const line = reviewLine(item);
  const isCounsel = item.review?.kind === 'counsel';

  const toggleText = async () => {
    if (showText) return setShowText(false);
    if (!policy) {
      setLoadingText(true);
      try {
        setPolicy(await fetchLocationPolicy());
      } catch (e) {
        onError(e);
        return;
      } finally {
        setLoadingText(false);
      }
    }
    setShowText(true);
  };

  const save = async () => {
    if (item.policyVersion === null) return;
    setSaving(true);
    try {
      onStatus(await recordLocationPolicyReview(item.policyVersion, parseStatesInput(states), reviewer, note.trim() || null));
      setEditing(false);
      setStates('');
      setReviewer('');
      setNote('');
    } catch (e) {
      onError(e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-2" data-testid="location-policy-review">
      {line && (
        <p className={`text-xs ${item.review?.kind === 'pilot_exemption' || !item.review ? 'text-amber-600 dark:text-amber-400' : 'text-muted-foreground'}`}>
          {line}
        </p>
      )}
      {item.review?.note && isCounsel && <p className="text-xs text-muted-foreground">Note: {item.review.note}</p>}

      <div className="flex flex-wrap gap-2">
        <Button type="button" size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => void toggleText()} disabled={loadingText}>
          {loadingText && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
          {showText ? COPY.hideText : COPY.readText}
        </Button>
        {canManage && !editing && (
          <Button type="button" size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setEditing(true)}>
            {isCounsel ? COPY.rerecord : COPY.record}
          </Button>
        )}
      </div>

      {showText && policy && <PolicyText policy={policy} />}

      {editing && (
        <div className="space-y-3 rounded-md border border-border p-3">
          <div className="space-y-1">
            <Label htmlFor="lp-states">{COPY.statesLabel}</Label>
            <Input id="lp-states" value={states} onChange={(e) => setStates(e.target.value)} placeholder={COPY.statesHint} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="lp-reviewer">{COPY.reviewerLabel}</Label>
            <Input id="lp-reviewer" value={reviewer} onChange={(e) => setReviewer(e.target.value)} placeholder={COPY.reviewerHint} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="lp-note">{COPY.noteLabel}</Label>
            <Textarea id="lp-note" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
          <div className="flex gap-2">
            <Button type="button" size="sm" onClick={() => void save()} disabled={saving}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {COPY.save}
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={() => setEditing(false)} disabled={saving}>
              {COPY.cancel}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

type Section = { heading?: unknown; points?: unknown };
const list = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []);

function PolicyText({ policy }: { policy: LocationPolicyText }) {
  const b = policy.body;
  const sections = [b.collected, b.who_sees, b.retention] as Section[];
  const m = (b.manager_sees ?? {}) as { heading?: unknown; intro?: unknown; sees?: unknown; does_not_see?: unknown };
  return (
    <div className="space-y-3 rounded-md border border-border bg-muted/30 p-3 text-sm" data-testid="location-policy-text">
      <p className="font-medium">{String(b.title ?? '')}</p>
      <p className="text-muted-foreground">{String(b.intro ?? '')}</p>
      {sections.map((s, i) => (
        <div key={i}>
          <p className="font-medium">{String(s?.heading ?? '')}</p>
          <ul className="ml-5 list-disc text-muted-foreground">{list(s?.points).map((p) => <li key={p}>{p}</li>)}</ul>
        </div>
      ))}
      <div>
        <p className="font-medium">{String(m.heading ?? '')}</p>
        {typeof m.intro === 'string' && <p className="text-muted-foreground">{m.intro}</p>}
        <p className="mt-1 text-xs font-medium uppercase text-muted-foreground">Sees</p>
        <ul className="ml-5 list-disc text-muted-foreground">{list(m.sees).map((p) => <li key={p}>{p}</li>)}</ul>
        <p className="mt-1 text-xs font-medium uppercase text-muted-foreground">Does not see</p>
        <ul className="ml-5 list-disc text-muted-foreground">{list(m.does_not_see).map((p) => <li key={p}>{p}</li>)}</ul>
      </div>
      <p className="text-xs text-muted-foreground">
        Version {policy.version}{policy.publishedOn ? `, published ${policy.publishedOn}` : ''}. Reps agree to it on the phone before their first Knock.
      </p>
    </div>
  );
}
